// frontend/src/components/WidgetManager.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Zap, Plus, Edit, Trash2, Eye, Cloud, Clock, 
  AlertCircle, CheckCircle, Settings, X, Rss, Globe
} from 'lucide-react';
import WeatherWidgetConfig from './widgets/WeatherWidgetConfig';
import ClockWidgetConfig from './widgets/ClockWidgetConfig';
import RSSWidgetConfig from './widgets/RSSWidgetConfig';
import StreamingWidgetConfig from './widgets/StreamingWidgetConfig';
import WebContentConfig from './widgets/WebContentConfig';
import MultiZoneConfig from './widgets/MultiZoneConfig';

const WidgetManager = () => {
  const { t } = useTranslation();
  const [activeFeatures, setActiveFeatures] = useState([]);
  const [widgetInstances, setWidgetInstances] = useState({});
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [editingInstance, setEditingInstance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');

      // Load active features
      const featuresResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/features`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const featuresData = await featuresResponse.json();
      
      // Filter only activated features
      const activated = featuresData.features.filter(f => f.isActivated);
      setActiveFeatures(activated);

      // Load instances for each activated feature
      const instancesData = {};
      for (const feature of activated) {
        const instancesResponse = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/features/${feature.id}/instances`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const data = await instancesResponse.json();
        instancesData[feature.id] = data.instances || [];
      }
      setWidgetInstances(instancesData);

    } catch (error) {
      console.error('Error loading widget data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper-Funktion: Übersetzt Feature-Namen und Beschreibungen
  const getTranslatedFeature = (feature) => {
    const translationKey = `marketplace.features.${feature.id}`;
    
    // Versuche Übersetzung zu laden
    const translatedName = t(`${translationKey}.name`, { defaultValue: '' });
    const translatedDescription = t(`${translationKey}.description`, { defaultValue: '' });
    
    // Wenn Übersetzung vorhanden, nutze sie, sonst Original vom Backend
    return {
      ...feature,
      name: translatedName || feature.name,
      description: translatedDescription || feature.description
    };
  };

  const handleCreateWidget = (feature) => {
    setSelectedFeature(feature);
    setEditingInstance(null);
    setShowConfig(true);
  };

  const handleEditWidget = (feature, instance) => {
    // 🔍 DEBUG: Log EVERYTHING
    console.log('🔧 handleEditWidget called');
    console.log('📦 feature:', feature);
    console.log('🆔 feature.id:', feature?.id);
    console.log('📊 Keys:', Object.keys(feature));

    setSelectedFeature(feature);
    setEditingInstance(instance);
    setShowConfig(true);
  };

  const handleDeleteWidget = async (featureId, instanceId) => {
    if (!window.confirm(t('widgets.deleteConfirm'))) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/features/${featureId}/instances/${instanceId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        loadData();
        alert(t('widgets.deleteSuccess'));
      } else {
        const error = await response.json();
        alert(`${t('common.error')}: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
      alert(t('widgets.deleteFailed'));
    }
  };

  const handleSaveWidget = async (config) => {
    try {
      // 🔍 DEBUG: Log what we're about to save
      console.log('💾 WidgetManager.handleSaveWidget called with:', {
        selectedFeature: selectedFeature?.id,
        editingInstance: editingInstance?.id,
        config: config
      });

      const token = localStorage.getItem('token');
      
      // ✅ Stelle sicher dass selectedFeature.id verwendet wird
      const featureId = selectedFeature.id;
      
      const url = editingInstance
        ? `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/features/${featureId}/instances/${editingInstance.id}`
        : `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/features/${featureId}/instances`;

      const method = editingInstance ? 'PUT' : 'POST';

      console.log('📡 Making request:', { method, url, featureId });

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: config.name,
          config: config.config
        })
      });

      console.log('📥 Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Success:', result);
        
        setShowConfig(false);
        setSelectedFeature(null);
        setEditingInstance(null);
        loadData();
        alert(editingInstance ? t('widgets.updateSuccess') : t('widgets.createSuccess'));
      } else {
        const error = await response.json();
        console.error('❌ Error response:', error);
        alert(`${t('common.error')}: ${error.error || error.message}`);
      }
    } catch (error) {
      console.error('❌ Exception in handleSaveWidget:', error);
      alert(t('widgets.saveFailed'));
    }
  };

  const getFeatureIcon = (featureId) => {
    switch (featureId) {
      case 'weather-widget': return <Cloud size={24} className="text-primary-600" />;
      case 'clock-widget': return <Clock size={24} className="text-purple-600" />;
      case 'rss-feed-widget': return <Rss size={24} className="text-orange-600" />;
      case 'web-content-widget': return <Globe size={24} className="text-green-600" />;
      default: return <Zap size={24} className="text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
          <Zap size={32} className="text-primary-600 dark:text-primary-400" />
          {t('widgets.widgetManagement')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('widgets.createAndManage')}
        </p>
      </div>

      {/* No Active Features */}
      {activeFeatures.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <AlertCircle className="mx-auto text-gray-400 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
            {t('widgets.noActiveFeatures')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('widgets.activateFeaturesFirst')}
          </p>
          <button
            onClick={() => window.location.hash = '#marketplace'}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('widgets.goToMarketplace')}
          </button>
        </div>
      )}

      {/* Feature Cards */}
      {activeFeatures.map(originalFeature => {
        console.log('🔄 originalFeature:', originalFeature.id, originalFeature.name);

        // ✅ Feature übersetzen
        const feature = getTranslatedFeature(originalFeature);
        console.log('✅ translated feature:', feature.id, feature.name);
        
        const instances = widgetInstances[feature.id] || [];
        const canCreateMore = feature.limits.maxInstances === null || 
                              instances.length < feature.limits.maxInstances;

        return (
          <div key={feature.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            {/* Feature Header */}
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-lg">
                    {getFeatureIcon(feature.id)}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{feature.name}</h3>
                    <p className="text-blue-100">{feature.description}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-3xl font-bold">{instances.length}</div>
                  <div className="text-sm text-blue-100">
                    {feature.limits.maxInstances 
                      ? t('widgets.ofMax', { max: feature.limits.maxInstances })
                      : t('widgets.instances')}
                  </div>
                </div>
              </div>
            </div>

            {/* Instances List */}
            <div className="p-6">
              {instances.length === 0 ? (
                <div className="text-center py-8">
                  <Settings className="mx-auto text-gray-400 mb-4" size={48} />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {t('widgets.noInstancesYet')}
                  </p>
                  {canCreateMore && (
                    <button
                      onClick={() => handleCreateWidget(originalFeature)}
                      className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 mx-auto"
                    >
                      <Plus size={20} />
                      {t('widgets.createFirstInstance')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {instances.map(instance => (
                    <div
                      key={instance.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                              {instance.name}
                            </h4>
                            <span className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs px-2 py-1 rounded-full font-medium">
                              {t('widgets.active')}
                            </span>
                          </div>
                          
                          {/* Config Preview */}
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {feature.id === 'weather-widget' && (
                              <div className="flex items-center gap-4">
                                <span>{instance.config.location}</span>
                                <span>{instance.config.units === 'metric' ? t('widgets.metric') : t('widgets.imperial')}</span>
                                <span>{instance.config.updateInterval}s</span>
                              </div>
                            )}
                            {feature.id === 'clock-widget' && (
                              <div className="flex items-center gap-4">
                                <span>⏰ {instance.config.format}</span>
                                <span>{instance.config.showDate ? t('widgets.withDate') : ''}</span>
                              </div>
                            )}
                            {feature.id === 'web-content-widget' && (
                              <div className="flex items-center gap-4">
                                <span>🌐 {instance.config.url?.substring(0, 50)}...</span>
                                <span>{instance.config.displayMode}</span>
                              </div>
                            )}
                          </div>

                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            {t('widgets.created')}: {new Date(instance.created_at).toLocaleString()}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* Preview Button */}
                          <button
                            onClick={() => window.open(`/widget/test/${instance.id}`, '_blank')}
                            className="p-2 text-primary-600 dark:text-primary-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title={t('widgets.previewWidget')}
                          >
                            <Eye size={18} />
                          </button>

                          <button
                            onClick={() => {
                              // ✅ FIX: Finde das richtige Feature per ID
                              const featureId = feature.id;
                              const correctFeature = activeFeatures.find(f => f.id === featureId);
                              console.log('🎯 Editing:', featureId, correctFeature?.name);
                              handleEditWidget(correctFeature, instance);
                            }}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title={t('common.edit')}
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteWidget(feature.id, instance.id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title={t('common.delete')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add More Button */}
                  {canCreateMore && (
                    <button
                      onClick={() => handleCreateWidget(originalFeature)}
                      className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-gray-600 dark:text-gray-400 hover:border-primary-500 dark:hover:border-blue-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus size={20} />
                      {t('widgets.createAnotherInstance')}
                    </button>
                  )}

                  {!canCreateMore && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 text-center">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        ⚠️ {t('widgets.instanceLimitReached', { max: feature.limits.maxInstances })}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Configuration Modal */}
      {showConfig && selectedFeature && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {(() => {
              // 🔍 DEBUG
              console.log('🎨 Rendering modal for:', selectedFeature.id);
              
              // ✅ if-else statt && - nur EINE Komponente wird gerendert!
              if (selectedFeature.id === 'weather-widget') {
                return (
                  <WeatherWidgetConfig
                    existingConfig={editingInstance}
                    onSave={handleSaveWidget}
                    onCancel={() => {
                      setShowConfig(false);
                      setSelectedFeature(null);
                      setEditingInstance(null);
                    }}
                  />
                );
              } else if (selectedFeature.id === 'clock-widget') {
                return (
                  <ClockWidgetConfig
                    existingConfig={editingInstance}
                    onSave={handleSaveWidget}
                    onCancel={() => {
                      setShowConfig(false);
                      setSelectedFeature(null);
                      setEditingInstance(null);
                    }}
                  />
                );
              } else if (selectedFeature.id === 'rss-feed-widget') {
                return (
                  <RSSWidgetConfig
                    existingConfig={editingInstance}
                    onSave={handleSaveWidget}
                    onCancel={() => {
                      setShowConfig(false);
                      setSelectedFeature(null);
                      setEditingInstance(null);
                    }}
                  />
                );
              } else if (selectedFeature.id === 'streaming-widget') {
                return (
                  <StreamingWidgetConfig
                    existingConfig={editingInstance}
                    onSave={handleSaveWidget}
                    onCancel={() => {
                      setShowConfig(false);
                      setSelectedFeature(null);
                      setEditingInstance(null);
                    }}
                  />
                );
              } else if (selectedFeature.id === 'web-content-widget') {
  return (
    <WebContentConfig
      existingConfig={editingInstance}
      onSave={handleSaveWidget}
                    onCancel={() => {
                      setShowConfig(false);
                      setSelectedFeature(null);
                      setEditingInstance(null);
                    }}
                  />
                );
} else if (selectedFeature.id === 'multi-zone') {
  return (
    <MultiZoneConfig
      existingConfig={editingInstance}
      onSave={handleSaveWidget}
      onCancel={() => {
        setShowConfig(false);
        setSelectedFeature(null);
        setEditingInstance(null);
      }}
    />
  );
              }
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetManager;